import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import Highlighter from "react-highlight-words";

import NormalTag from "../../../../components/tags/NormalTag";
import { Colors } from "../../../../constants/Colors";
import { NormalTagTypes } from "../../../../types/types";
import type { VideoType, SongType } from "./faceCallTypes";

const getColumnSearchProps = <T extends object>(
  dataIndex: keyof T,
  searchInput: any,
  searchText: string,
  searchedColumn: string,
  handleSearch: any,
  handleReset: any,
): ColumnType<T> => ({
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    close,
  }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        ref={searchInput}
        placeholder={`Search ${String(dataIndex)}`}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(selectedKeys as string[], confirm, dataIndex)
        }
        style={{ marginBottom: 8, display: "block" }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={() => clearFilters && handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Clear
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
        >
          close
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
  ),
  onFilter: (value, record) =>
    String(record[dataIndex])
      .toLowerCase()
      .includes((value as string).toLowerCase()),
  onFilterDropdownOpenChange: (visible) => {
    if (visible) {
      setTimeout(() => searchInput.current?.select(), 100);
    }
  },
  render: (text) =>
    searchedColumn === dataIndex ? (
      <Highlighter
        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text ? text.toString() : ""}
      />
    ) : (
      text
    ),
});

export function createFaceCallVideosColumns(
  dateFiltersPerformer: any,
  dateFiltersTheme: any,
  displayType: number,
  searchInput: any,
  searchText: string,
  searchedColumn: string,
  selectedPerformers: string[] | null,
  selectedThemes: string[] | null,
  isAdmin: boolean,
  handleSearch: any,
  handleReset: any,
  handleEdit: (record: VideoType) => void,
  handleDelete: (videoId: number) => void,
): ColumnsType<VideoType> {
  return [
    {
      title: '#',
      dataIndex: 'videoId',
      key: 'videoId',
      width: 70,
    },
    {
      title: 'Song title',
      dataIndex: 'songTitle',
      key: 'songTitle',
      ...getColumnSearchProps<VideoType>(
        "songTitle",
        searchInput,
        searchText,
        searchedColumn,
        handleSearch,
        handleReset
      ),
    },
    {
      title: 'Performer',
      dataIndex: 'performerName',
      key: 'performerName',
      filters: dateFiltersPerformer,
      filteredValue: selectedPerformers,
      sorter: (a, b) => a.performerName.localeCompare(b.performerName),
      onFilter: (value, record) => record.performerName === value,
    },
    {
      title: 'Publish date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (a, b) => a.startDate.localeCompare(b.startDate),
    },
    ...(displayType === 4
      ? [
        {
          title: 'Theme',
          dataIndex: 'themeId',
          key: 'themeId',
          filters: dateFiltersTheme,
          filteredValue: selectedThemes,
          sorter: (a: any, b: any) => a.startDate.localeCompare(b.startDate),
          onFilter: (value: any, record: VideoType) =>
            record.themeId === Number(value),
          render: (value: number) => {
            const themeName = dateFiltersTheme.find((item: any) => Number(item.value) === Number(value))?.text ?? '';

            if (
              themeName === NormalTagTypes.CHRISTMAS ||
              themeName === NormalTagTypes.HALLOWEEN ||
              themeName === NormalTagTypes.NEW_YEAR
            ) {
              return <NormalTag type={themeName} />;
            }

            return themeName;
          },
        },
      ]
      : []),
    ...(isAdmin ?
      [
        {
          title: "Action",
          dataIndex: "",
          key: "",
          width: 120,
          render: (_: any, record: any) => (
            <div className="flex gap-4">
              <Button type="link" className="w-9 p-0" onClick={() => handleEdit(record)}>
                Edit
              </Button>
              <Button type="link" className="w-9 p-0" onClick={() => handleDelete(record.videoId)}>
                Delete
              </Button>
            </div>
          ),
        },
      ]
      : []),
  ];
}

export function createFaceCallSongsColumns(
  dateFilters: any,
  searchInput: any,
  searchText: string,
  searchedColumn: string,
  handleSearch: any,
  handleReset: any,
): ColumnsType<SongType> {
  return [
    {
      title: (
        <span>
          Song title
          {searchedColumn === 'songTitle' && searchText && (
            <span style={{ color: Colors.blue, marginLeft: 8 }}>
              (Searching: {searchText})
            </span>
          )}
        </span>
      ),
      dataIndex: 'songTitle',
      key: 'songTitle',
      sorter: (a, b) => a.songTitle.localeCompare(b.songTitle),
      ...getColumnSearchProps<SongType>(
        "songTitle",
        searchInput,
        searchText,
        searchedColumn,
        handleSearch,
        handleReset
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      filters: dateFilters,
      sorter: (a, b) => a.category.localeCompare(b.category),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Song ID number',
      dataIndex: 'songId',
      key: 'songId',
    },
  ];
}